﻿import { useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { findMarket, marketPath, slugify } from "../../seo/markets.mjs";
import { states } from "../../util/StateUtil";

export interface LocationSearchParams {
  street_number?: string;
  street_name?: string;
  city?: string;
  state?: string;
  country?: string;
  place_id?: string;
  lat?: string;
  lng?: string;
}

export interface SetLocationOptions {
  /** Use history replace instead of push (e.g. while dragging the map, to avoid stacking back-history entries). */
  replace?: boolean;
}

type PathPlace = Required<Pick<LocationSearchParams, 'city' | 'state' | 'country'>>;

const CITY_PATH = /^\/yards\/([a-z]{2})\/([a-z]{2})\/([^/]+)\/?$/i;

// Google's short locality names, which old /search links carried before
// securspace-ui#3048 (city=KCMO, city=SF): read them as the real city.
const SLUG_ALIASES: Readonly<Record<string, string>> = {'us/mo/kcmo': 'kansas-city', 'us/ca/sf': 'san-francisco'};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment; // a malformed escape must not break the page
  }
};

/**
 * The place a /yards/<country>/<state>/<city> address names (securspace-ui#3052): a
 * market by its market-list name, never the geocoder's (Kansas City, not KCMO), and
 * any other city de-slugified. Undefined for every other address.
 */
export const placeFromYardsPath = (pathname: string): PathPlace | undefined => {
  const [, country, state, segment] = CITY_PATH.exec(pathname) ?? [];
  if (!segment) return undefined;
  // Underscores separate words too (los_angeles); slugify keeps them.
  const readSlug = slugify(decodeSegment(segment).replace(/_/g, '-'));
  const slug = SLUG_ALIASES[`${country}/${state}/${readSlug}`.toLowerCase()] ?? readSlug;
  const market = findMarket(country, state, slug);
  if (market) return {city: market.city, state: market.state, country: market.country};
  const city = slug.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').trim();
  return city ? {city, state: state.toUpperCase(), country: country.toUpperCase()} : undefined;
};

/** Where a /yards address belongs: its lowercase city path, or /yards for any other shape. */
export const canonicalYardsPath = (pathname: string): string => {
  const place = placeFromYardsPath(pathname);
  return place ? marketPath(place) : '/yards';
};

const QUERY_KEYS: ReadonlyArray<keyof LocationSearchParams> = ['street_number', 'street_name', 'city', 'state', 'country', 'place_id', 'lat', 'lng'];
const PATH_KEYS = new Set<keyof LocationSearchParams>(['city', 'state', 'country']);

/** The /yards city path for a place, or undefined when it can't be one (it then stays in the query). */
const yardsPathFor = (place: LocationSearchParams | undefined): string | undefined => {
  if (!place?.city || !place.state) return undefined;
  const path = canonicalYardsPath(marketPath({
    city: place.city,
    state: states.get(place.state) || place.state,
    // The search box sends "USA"; every yard is in the US today.
    country: /^[a-z]{2}$/i.test(place.country ?? '') ? place.country : 'US',
  }));
  return path === '/yards' ? undefined : path;
};

/**
 * The /yards address for a search location: the place in the path, everything else
 * in the query (securspace-ui#3053). Other params already in `search` (sortBy, utm_*)
 * stay where they are. With no place, or one that can't be a path, it's /yards?….
 */
export const yardsAddress = (place: LocationSearchParams | undefined, search = ''): {pathname: string, search: string} => {
  const path = yardsPathFor(place);
  const params = new URLSearchParams(search);
  for (const key of QUERY_KEYS) {
    const value = path && PATH_KEYS.has(key) ? undefined : place?.[key];
    if (value) params.set(key, value);
    else params.delete(key);
  }
  // Normalize `long` → `lng`: remove the alias so the URL always uses `lng`
  params.delete('long');
  // TODO (future ticket): remove state_region_dropdown if it's not needed
  const query = params.toString();
  return {pathname: path ?? '/yards', search: query ? `?${query}` : ''};
};

/**
 * The search location in the URL, a setter for it, and whether the place came from a
 * /yards city path. City, state and country come from the path, unless the query
 * names a city and state (legacy links). Everything else is always in the query.
 *
 * Writes go to the /yards address for the location (see yardsAddress), keeping the
 * hash. /search only redirects there now (securspace-ui#3054). A write that wouldn't
 * change the address adds no history entry.
 */
export const useLocationSearchParams: () => [LocationSearchParams | undefined, (newValue: LocationSearchParams | undefined, options?: SetLocationOptions) => void, boolean] = () => {
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const {pathname} = routerLocation;
  // Create a stable string representation of searchParams for useMemo dependency
  const searchParamsString = searchParams.toString();
  const pathPlace = useMemo(() => placeFromYardsPath(pathname), [pathname]);
  const cityFromPath = !!pathPlace && !(searchParams.get('city') && searchParams.get('state'));

  // Derive location directly from searchParams
  const location = useMemo(() => {
    const currentStreetNumber = searchParams.get('street_number');
    const currentStreetName = searchParams.get('street_name');
    const currentCity = cityFromPath ? pathPlace.city : searchParams.get('city');
    const currentState = cityFromPath ? pathPlace.state : searchParams.get('state');
    const currentCountry = cityFromPath ? pathPlace.country : searchParams.get('country');
    const currentPlaceId = searchParams.get('place_id');
    const currentLat = searchParams.get('lat');
    const currentLng = searchParams.get('lng') || searchParams.get('long');

    // Helper to attach lat/lng when present
    const withLatLng = (params: LocationSearchParams): LocationSearchParams => {
      if (currentLat) params.lat = currentLat;
      if (currentLng) params.lng = currentLng;
      return params;
    };

    if (currentStreetNumber && currentStreetNumber.trim() !== "" &&
        currentStreetName && currentStreetName.trim() !== "" &&
        currentCity && currentState && currentCountry) {
      return withLatLng({
        street_number: currentStreetNumber,
        street_name: currentStreetName,
        city: currentCity,
        state: currentState,
        country: currentCountry,
        place_id: currentPlaceId || ""
      });
    } else if (currentCity && currentState && currentCountry) {
      return withLatLng({
        street_number: '',
        street_name: '',
        city: currentCity,
        state: currentState,
        country: currentCountry,
        place_id: currentPlaceId || ""
      });
    } else if (currentCity && currentState) {
      return withLatLng({
        city: currentCity,
        state: currentState,
        place_id: currentPlaceId || ""
      });
    } else if (currentLat && currentLng) {
      // Support lat/lng-only URLs (no city/state) — the cascade will reverse-geocode
      return {
        lat: currentLat,
        lng: currentLng,
        place_id: currentPlaceId || ""
      };
    }

    return undefined;
    // searchParams is a stable reference, so we need to use the string representation to detect changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, searchParamsString, pathPlace, cityFromPath]);

  // Refs keep the setter's identity stable while it always reads the latest address
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const routerLocationRef = useRef(routerLocation);
  routerLocationRef.current = routerLocation;

  // Setter function that updates the URL (memoized for stable reference)
  const setLocation = useCallback((newLocation: LocationSearchParams | undefined, options?: SetLocationOptions) => {
    const {pathname, search, hash} = routerLocationRef.current;
    const next = yardsAddress(newLocation, search);
    const unchanged = next.pathname === pathname && new URLSearchParams(next.search).toString() === new URLSearchParams(search).toString();
    if (unchanged) return;
    navigateRef.current({...next, hash}, {replace: !!options?.replace});
  }, []);

  return [location, setLocation, cityFromPath];
}
